import { HttpError } from '@forto-dev/ui-platform-http'
import { createContext, FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useRootStore } from 'service/useRootStore'
import { useI18n } from 'utils/localisation'
import { logError } from 'utils/logger'

import { ShipmentBookmarkWithId } from '../domain/ShipmentBookmark'
import { useUserProfileCoreApi } from './useUserProfileCoreApi'

export type UseShipmentBookmark = {
  addBookmark: (shipmentId: string) => Promise<void>
  bookmarkedShipmentIds: string[]
  removeBookmark: (shipmentId: string) => Promise<void>
  isLoadingBookmarks: boolean
}

const translationKeys = {
  bookmarkSuccessMessage: 'shipment_bookmark-success-message',
  removeBookmarkSuccessMessage: 'shipment_remove-bookmark-success-message',
  bookmarkServerErrorMessage: 'error_popup-server_error_text',
}

export const ShipmentBookmarkContext = createContext<UseShipmentBookmark>({} as UseShipmentBookmark)

export const ShipmentBookmarkContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [bookmarks, setBookmarks] = useState<ShipmentBookmarkWithId[]>([])
  const [isLoadingBookmarks, setIsLoadingBookmarks] = useState<boolean>(true)
  const {
    bookmarks: { getBookmarkedShipments, saveBookmark, removeBookmark: removeBookmarkAPI },
  } = useUserProfileCoreApi()
  const { uiStore } = useRootStore()
  const { translate } = useI18n()

  const getBookmarks = useCallback(async (): Promise<void> => {
    try {
      const bookmarkResponse = await getBookmarkedShipments()
      setBookmarks(bookmarkResponse ?? [])
      setIsLoadingBookmarks(false)
    } catch (e) {
      /** Temporary workaround to clean unauthorized errors from logs
       * @todo Move all error logging to HTTP service */
      if ((e as HttpError)?.status !== 401) {
        logError(e as Error)
        uiStore.snack.addSnack({
          type: 'error',
          message: translate(translationKeys.bookmarkServerErrorMessage),
        })
      }
    }
  }, [getBookmarkedShipments, translate, uiStore.snack])

  useEffect(() => {
    getBookmarks()
  }, [])

  const state: UseShipmentBookmark = useMemo(() => {
    const getBookmarkId = (shipmentId: string): string => {
      // @ts-expect-error TS(2322) FIXME: Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
      return bookmarks.find(({ shipmentId: id }) => id === shipmentId)?.bookmarkId
    }

    const bookmarkedShipmentIds: string[] = bookmarks?.map((ownerObject) => ownerObject.shipmentId) ?? []

    const addBookmark = async (shipmentId: string) => {
      try {
        const bookmarkId = await saveBookmark({ shipmentId })
        setBookmarks((prevState) => [...prevState, { shipmentId, bookmarkId }])
        uiStore.snack.addSnack({
          type: 'success',
          message: translate(translationKeys.bookmarkSuccessMessage, {
            shipmentId,
          }),
        })
      } catch (error) {
        // @ts-expect-error TS(2345) FIXME: Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
        logError(error)
        uiStore.snack.addSnack({
          type: 'error',
          message: translate(translationKeys.bookmarkServerErrorMessage),
        })
      }
    }

    const removeBookmark = async (shipmentId: string) => {
      const bookmarkId: string = getBookmarkId(shipmentId)
      try {
        await removeBookmarkAPI(bookmarkId)
        setBookmarks((prevState) => [...prevState.filter(({ bookmarkId: id }) => id !== bookmarkId)])
        uiStore.snack.addSnack({
          type: 'success',
          message: translate(translationKeys.removeBookmarkSuccessMessage),
        })
      } catch (error) {
        // @ts-expect-error TS(2345) FIXME: Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
        logError(error)
        uiStore.snack.addSnack({
          type: 'error',
          message: translate(translationKeys.bookmarkServerErrorMessage),
        })
      }
    }

    return {
      addBookmark,
      bookmarkedShipmentIds,
      removeBookmark,
      isLoadingBookmarks,
    }
  }, [isLoadingBookmarks, bookmarks, translate, uiStore.snack])

  return <ShipmentBookmarkContext.Provider value={state}>{children}</ShipmentBookmarkContext.Provider>
}

export const useShipmentBookmark = (): UseShipmentBookmark => {
  const context = useContext(ShipmentBookmarkContext)

  if (!context) {
    throw new Error('Must use useShipmentBookmark in ShipmentBookmarkContext')
  }

  return context
}
